<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import ModulePicker from '@/components/ModulePicker.vue'
import TermField from '@/components/TermField.vue'
import UiButton from '@/components/UiButton.vue'
import UiModal from '@/components/UiModal.vue'
import { ApiError } from '@/api/client'
import * as ls from '@/api/ls'
import type { AddonIssueInput, License, LicenseAddon, PlanSpec, ProductSpec, QuotaSpec } from '@/api/ls'
import { addonQuotaInputAttrs, addonQuotaInputMeta, addonStepError, bumpAddonQuota } from '@/lib/addonQuotaInput'
import { knownErrorText } from '@/lib/apiErrors'
import { isInternalPlan } from '@/lib/products'
import { quotaAddonStep } from '@/lib/quotas'
import { MINUTES_PER_DAY, formatTerm, splitMinutes, totalMinutes } from '@/lib/terms'
import { useDialogsStore } from '@/stores/dialogs'

/**
 * 加購子憑證（IPOADD1）的簽發表單（批93-B §2.3）。
 *
 * ## 兩條路，同一組欄位
 * 先選「加購套裝」（`product_plans.kind='addon'`）或全自訂。選了套裝就**只送 key**，
 * 內容由伺服器端展開（`services/addons._plan_content`）——讓前端把展開後的值送回來，
 * 等於模板只是個下拉選單的裝飾，而批93-C 的政策引擎正是靠「內容是否等於模板」判定
 * 誰可以免審核發。
 *
 * ## 配額為什麼不用 `QuotaEditor`
 * 那支的語意與加購**相反**：它每鍵齊帶（缺席會被補成 `absent_default`）、用 `null` 表達
 * 「無上限」。加購的規則是「**鍵缺席＝不影響、`null`＝拒簽**」——直接複用會把使用者根本
 * 沒碰的配額也一起寫進子證（等於偷偷改上限），而「無上限」那個勾更是後端 422。
 * 所以這裡是逐列 opt-in 的「調整」勾選：勾了才進 payload，送的就是輸入框的值。
 *
 * ★ 批151（裁示 98；覆核 🟡3）：輸入框**填什麼**依該配額鍵宣告的子證合併方式
 *   （`lib/addonQuotaInput.ts`）。預設 `max`＝目標值（「現行 5000 ＋1000」助手算完送 6000）；
 *   `sum`（加總）＝**增加量**，助手不讀主證現值（否則主證 1000、+100 會送 1100、合併成 2100）；
 *   `min`／`avg` 不提供助手、只附說明；`latest` 同 `max`。
 *
 * `ModulePicker` 則可以直接複用：它的 `build()` 本來就會略過 `none` 的列，
 * 「沒勾＝鍵不存在」與加購語意天然一致。
 */
const props = defineProps<{
  open: boolean
  customerId: string
  /** 綁定的主機。**必填**——子證沒有它就等於客戶名下每一台都吃得到。 */
  instanceId: string
  /** 主證（帶得到就帶：拿它顯示現行配額、比對到期日、寫進 payload 的 `issued_against`）。 */
  license?: License | null
  /** 沒有主證時必須顯式指定產品（`license` 有值時以它為準）。 */
  product?: string | null
  /** 該產品的憑證設計（模組／配額的中文名與結構）。 */
  spec: ProductSpec | null
  /** 這台目前有效的加購——用來算「已達 16 張」、顯示已加購過什麼，以及「取代」下拉的選項。 */
  activeAddons?: LicenseAddon[]
  /** 批155：可以選內部子證模板／勾內部模組（我方頁面傳 `auth.isManagerOrAbove`）。缺省＝不可以。 */
  allowInternal?: boolean
}>()
const emit = defineEmits<{ close: []; issued: [LicenseAddon] }>()

const dialogs = useDialogsStore()

const MAX_ACTIVE_ADDONS = 16 // 與 `services/addons.MAX_ACTIVE_ADDONS` 同值
/**
  * 期限快捷鍵（單位**總分鐘**，批108）。前兩個是本批新增的短期情境
  * ——技術支援救援與教育訓練，用完就該換回正式證。
  */
const TERM_CHOICES: number[] = [30, 120, 30 * MINUTES_PER_DAY, 90 * MINUTES_PER_DAY,
  180 * MINUTES_PER_DAY, 365 * MINUTES_PER_DAY]

const planKey = ref('')
const label = ref('')
const modules = ref<Record<string, string>>({})
/** 逐配額鍵的「要不要調整」＋輸入值（只有勾選的會進 payload；填目標值或增加量依合併方式）。 */
const quotaOn = ref<Record<string, boolean>>({})
const quotaTo = ref<Record<string, string>>({})
const termMode = ref<'days' | 'date'>('days')
/** 期限**總分鐘**（`term_days * 1440 + term_minutes`）；送出前用 `splitMinutes` 拆兩欄。 */
const termTotal = ref(90 * MINUTES_PER_DAY)
const expiresAt = ref('')
const notBefore = ref('')
const trial = ref(false)
const notes = ref('')
/** 批155：取代哪一張（`addon_id`；空＝不取代，另外加一張）。 */
const supersedes = ref('')
const busy = ref(false)
/** ModulePicker／配額列的重掛鍵——套用套裝或換產品時 bump（那兩支只在掛載時 initFrom）。 */
const formKey = ref(0)

const productKey = computed(() => props.license?.product ?? props.product ?? '')
/** 批155：內部子證模板只有營運主管以上列得出來。 */
const addonPlans = computed<PlanSpec[]>(() =>
  (props.spec?.plans ?? []).filter(
    (p) => p.kind === 'addon' && p.is_active && (!isInternalPlan(p) || props.allowInternal),
  ),
)
const selectedPlan = computed<PlanSpec | null>(() => addonPlans.value.find((p) => p.key === planKey.value) ?? null)
const quotaFields = computed(() => props.spec?.quotas ?? [])
/**
 * 佔名額的子證＝有效而且**未過期**（批155：過期的不算進 16 張，下一次簽發前後端會把它們收斂成
 * 「已被取代」）。
 */
const activeCount = computed(
  () =>
    (props.activeAddons ?? []).filter(
      (a) => a.status === 'active' && (!a.expires_at || Date.parse(a.expires_at) > Date.now()),
    ).length,
)

/**
 * 批155：「取代」下拉的選項＝這台主機上**有效**、而且與選的模板**同一個子證模板**的子證
 * （後端規則：同一個 instance、同一個子證模板、狀態 active，否則 409 `supersedes_invalid`；
 * 自訂內容的子證沒有模板，不能被取代、也不能取代別張）。
 * 投影沒帶 `addon_plan`（舊後端）時退回列出全部有效的，交給後端判。
 */
const supersedeOptions = computed<LicenseAddon[]>(() =>
  (props.activeAddons ?? []).filter((a) => {
    if (a.status !== 'active') return false
    if (a.addon_plan === undefined) return true
    return !!planKey.value && a.addon_plan === planKey.value
  }),
)
/** 「取代」下拉停用時的那一行字（沒選模板／這台沒有同模板的有效子證）。 */
const supersedeEmptyText = computed(() =>
  planKey.value ? '（這台主機沒有同一個模板的有效子證）' : '（先選子證模板；自訂內容不能取代）',
)
// 換了模板之後原本選的那張不在選項裡了 → 清掉（否則送出一張後端一定 409 的取代）。
watch(supersedeOptions, (opts) => {
  if (supersedes.value && !opts.some((a) => a.addon_id === supersedes.value)) supersedes.value = ''
})

function addonOptionText(a: LicenseAddon): string {
  const q = Object.entries(a.quotas ?? {})
    .map(([k, v]) => `${quotaFields.value.find((f) => f.key === k)?.label ?? k} ${v}`)
    .join('、')
  const exp = a.expires_at ? `到期 ${a.expires_at.slice(0, 10)}` : '未蓋到期日'
  return [a.label, q, exp, a.addon_id.slice(0, 8)].filter(Boolean).join('・')
}

/** 主證目前這個配額鍵的值：數字／`null`（不限）／`undefined`（鍵缺席）。 */
function currentQuota(key: string): number | null | undefined {
  const q = props.license?.quotas
  if (!q) return undefined
  return q[key]
}
function currentQuotaText(key: string): string {
  const v = currentQuota(key)
  if (v === undefined) return '未設定'
  if (v === null) return '不限'
  return String(v)
}

function reset() {
  planKey.value = ''
  label.value = ''
  modules.value = {}
  quotaOn.value = {}
  quotaTo.value = {}
  termMode.value = 'days'
  termTotal.value = 90 * MINUTES_PER_DAY
  expiresAt.value = ''
  notBefore.value = ''
  trial.value = false
  notes.value = ''
  supersedes.value = ''
  formKey.value++
}
watch(() => props.open, (o) => o && reset())

/** 選套裝＝把模板的內容帶進表單當**預覽**（送出時仍只送 key，由後端展開）。 */
function applyPlan() {
  const plan = addonPlans.value.find((p) => p.key === planKey.value)
  if (!plan) return
  label.value = plan.label
  modules.value = { ...plan.modules }
  quotaOn.value = {}
  quotaTo.value = {}
  for (const [k, v] of Object.entries(plan.quotas ?? {})) {
    // 模板的 `null`＝「不設定」→ 加購語意是「不動這一項」（後端展開時同樣會丟掉）。
    if (v == null) continue
    quotaOn.value[k] = true
    quotaTo.value[k] = String(v)
  }
  trial.value = plan.trial
  // ★ 批108 truthy 陷阱：`if (plan.term_days)` 會漏掉「2 小時體驗」這種模板
  //   （`term_days` 是 0、分鐘才有值）——一律比總分鐘。
  const planTotal = totalMinutes(plan.term_days, plan.term_minutes)
  if (planTotal > 0) {
    termMode.value = 'days'
    termTotal.value = planTotal
  }
  formKey.value++
}

/** 每個配額鍵的填法（依產品宣告的子證合併方式，見 `lib/addonQuotaInput.ts`）。 */
const quotaMeta = computed(() =>
  Object.fromEntries(quotaFields.value.map((f: QuotaSpec) => [f.key, addonQuotaInputMeta(f)])),
)

/**
 * 「＋N」助手：`max`／`latest` 以現行值為底加上去、寫回目標值；`sum` 只累加輸入框（填的就是
 * 增加量，不讀主證）；`min`／`avg` 不提供（按鈕本來就不顯示）。
 */
function bump(key: string, delta: number) {
  const field = quotaFields.value.find((f: QuotaSpec) => f.key === key)
  const next = bumpAddonQuota(
    quotaMeta.value[key]?.bump ?? null,
    quotaTo.value[key],
    currentQuota(key),
    delta,
    quotaAddonStep(field),
  )
  if (next !== null) quotaTo.value[key] = next
}

/** 每個配額鍵輸入框的 `min`／`step`（宣告了加購級距＝一階為單位）。 */
const quotaAttrs = computed(() =>
  Object.fromEntries(quotaFields.value.map((f: QuotaSpec) => [f.key, addonQuotaInputAttrs(f)])),
)

/**
 * 加購數量級距（11-01 裁示）：勾選了、填了值卻不是級距的倍數 → 那一列紅字、擋送出。
 * 只看**會送出去**的值：選了套裝時配額由後端展開（下方只是預覽，模板本身在存檔時已驗過級距）。
 * 空白與 0 不在這一關擋（`quotaPayload` 本來就不送它們）。
 */
const quotaStepIssues = computed<Record<string, string>>(() => {
  const out: Record<string, string> = {}
  if (planKey.value) return out
  for (const f of quotaFields.value) {
    if (!quotaOn.value[f.key]) continue
    const msg = addonStepError(f, quotaTo.value[f.key], { allowUnset: true })
    if (msg) out[f.key] = msg
  }
  return out
})

const quotaPayload = computed<Record<string, number> | null>(() => {
  const out: Record<string, number> = {}
  for (const f of quotaFields.value) {
    if (!quotaOn.value[f.key]) continue
    const n = Number(quotaTo.value[f.key])
    if (Number.isFinite(n) && n > 0) out[f.key] = Math.floor(n)
  }
  return Object.keys(out).length ? out : null
})

const hasContent = computed(
  () => Object.keys(modules.value).length > 0 || quotaPayload.value !== null,
)

// --- 即時警示 ---------------------------------------------------------------

/** 到期晚於主證（黃字不擋）：加購撐得比主證久，主證失效時它也不會生效。 */
const expiresLaterThanLicense = computed(() => {
  const licExp = props.license?.expires_at
  if (!licExp) return false
  const mine =
    termMode.value === 'date'
      ? expiresAt.value
      : new Date(Date.now() + termTotal.value * 60000).toISOString().slice(0, 10)
  return Boolean(mine) && mine > licExp.slice(0, 10)
})

/** 模組鍵不在這個產品的憑證設計裡（ModulePicker 會原樣保留未知鍵）。 */
const unknownModuleKeys = computed(() => {
  const known = new Set((props.spec?.modules ?? []).map((m) => m.key))
  return Object.keys(modules.value).filter((k) => !known.has(k))
})

// 取代一張＝簽出新張的同時舊張失效，名額不會多佔。
const atCap = computed(() => activeCount.value - (supersedes.value ? 1 : 0) >= MAX_ACTIVE_ADDONS)

const canSubmit = computed(
  () =>
    !busy.value &&
    !atCap.value &&
    Boolean(productKey.value) &&
    Boolean(planKey.value || label.value.trim()) &&
    (planKey.value ? true : hasContent.value) &&
    !Object.keys(quotaStepIssues.value).length &&
    (termMode.value === 'days' ? termTotal.value > 0 : Boolean(expiresAt.value)),
)

function body(): AddonIssueInput {
  const out: AddonIssueInput = { trial: trial.value }
  if (planKey.value) out.addon_plan_key = planKey.value
  if (label.value.trim()) out.label = label.value.trim()
  // 選了套裝就讓後端展開內容；沒選才把表單的值送過去。
  if (!planKey.value) {
    if (Object.keys(modules.value).length) out.modules = modules.value
    // 批114：數量一律走 `quotas`（上面那張 opt-in 表的鍵集合就是憑證設計的 `spec.quotas`，
    // 本來就含所有數量鍵）——原本另外那兩個一級數字欄是同一個數字的第二個輸入框。
    if (quotaPayload.value) out.quotas = quotaPayload.value
  }
  if (termMode.value === 'date') {
    out.expires_at = expiresAt.value
  } else {
    const [days, minutes] = splitMinutes(termTotal.value)
    out.term_days = days
    out.term_minutes = minutes
  }
  if (notBefore.value) out.not_before = notBefore.value
  if (notes.value.trim()) out.notes = notes.value.trim()
  if (supersedes.value) out.supersedes = supersedes.value
  return out
}

async function submit() {
  if (!canSubmit.value) return
  busy.value = true
  try {
    // 主證綁得到主機就走主入口（後端自動填 `issued_against`）；否則顯式帶 instance_id。
    const res = props.license?.host_instance_id
      ? await ls.issueLicenseAddon(props.license.license_id, body())
      : await ls.issueCustomerAddon(
          props.customerId,
          { ...body(), instance_id: props.instanceId },
          productKey.value,
        )
    const warn: string[] = []
    if (res.unknown_modules.length) warn.push(`模組鍵不在憑證設計：${res.unknown_modules.join('、')}`)
    if (res.unknown_module_items.length) warn.push(`子項不存在：${res.unknown_module_items.join('、')}`)
    if (res.unknown_quota_keys.length) warn.push(`配額鍵不在憑證設計：${res.unknown_quota_keys.join('、')}`)
    if (res.offline_warning) warn.push(res.offline_warning)
    emit('issued', res)
    emit('close')
    if (warn.length) {
      await dialogs.notify({ title: '已簽發（有提醒）', message: warn.join('\n') })
    }
  } catch (e) {
    const detail =
      e instanceof ApiError && e.payload ? (e.payload as { detail?: unknown }).detail : undefined
    // 批155：`supersedes_invalid`／內部模板與模組等跨畫面共用的碼先查中文對照。
    const msg =
      knownErrorText(detail) ??
      (detail == null
        ? '簽發失敗'
        : typeof detail === 'string'
          ? detail
          : ((detail as { message?: string }).message ?? JSON.stringify(detail)))
    await dialogs.notify({ title: '簽發失敗', message: msg })
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <UiModal :open="open" title="加購模組" width="860px" @close="emit('close')">
    <p class="ad-note">
      加購是一張<strong>獨立的子憑證</strong>：可單獨撤銷、可自己到期，主憑證一個位元都不動。
      模組取聯集；配額依產品對每一項宣告的<strong>合併方式</strong>——預設取<strong>最大值</strong>
      （填<strong>目標值</strong>，不是增量），標「加總」的項目填<strong>增加量</strong>。
      綁定的是<strong>主機</strong>不是這張證，續約換證加購照樣還在。
    </p>

    <p v-if="atCap" class="ad-err">
      這台部署已有 {{ activeCount }} 張有效加購（上限 {{ MAX_ACTIVE_ADDONS }} 張）。
      加購多到這個程度請改為<strong>重簽主憑證</strong>。
    </p>

    <div class="ad-grid">
      <label class="ad-f">
        <span>加購套裝</span>
        <select v-model="planKey" @change="applyPlan()">
          <option value="">（自訂）</option>
          <option v-for="p in addonPlans" :key="p.key" :value="p.key">{{ p.label }}</option>
        </select>
        <small v-if="!addonPlans.length" class="ad-hint">
          這個產品尚無加購套裝，可自訂；要建模板到〈產品 → 套裝〉新增 kind＝addon 的列。
        </small>
        <small v-else-if="planKey" class="ad-hint">
          內容由伺服器端依模板展開（下方顯示的是預覽）。
        </small>
      </label>
      <label class="ad-f">
        <span>名稱</span>
        <input v-model="label" maxlength="120" placeholder="AI 自動客服 90 天" />
        <small class="ad-hint">客戶在授權頁看得到這個名稱。</small>
      </label>
      <!-- 批155：取代同一台主機上的某一張（續期、調高或調低數值）。說明固定寫在下面，選不選都不動版面。 -->
      <label class="ad-f wide">
        <span>取代（選填）</span>
        <select v-model="supersedes" :disabled="!supersedeOptions.length">
          <option value="">
            {{ supersedeOptions.length ? '（不取代，另外加一張）' : supersedeEmptyText }}
          </option>
          <option v-for="a in supersedeOptions" :key="a.addon_id" :value="a.addon_id">
            {{ addonOptionText(a) }}
          </option>
        </select>
        <small class="ad-hint">
          選了＝新張簽出的同時，被取代那張在同一筆交易改成「已被取代」；新張填的是<strong>完整數值</strong>
          （不是在舊張上再加），可以比舊張小。只能取代同一台主機、同一個子證模板、仍然有效的子證。
        </small>
      </label>
    </div>

    <ModulePicker :key="`mods-${formKey}`" v-model="modules" :spec="spec" :allow-internal="allowInternal" />

    <fieldset class="ad-box">
      <legend>期限</legend>
      <!-- 批155：跟隨主證到期的模板——到期日以這台主機的現行主證為準，主證換發時自動換發新張。 -->
      <p v-if="selectedPlan?.follow_main_expiry" class="ad-hint follow">
        這個模板<strong>跟隨主證到期</strong>：簽出來的到期日等於這台主機現行主證的到期日（主證不到期時它也不到期），
        主證續約、延期或換證時會自動換發新張（數值不變）。
      </p>
      <div class="ad-term">
        <label><input v-model="termMode" type="radio" value="days" /> 期限</label>
        <span v-if="termMode === 'days'" class="ad-chips">
          <button
            v-for="m in TERM_CHOICES"
            :key="m"
            type="button"
            class="chip pick"
            :class="{ on: termTotal === m }"
            @click="termTotal = m"
          >
            {{ formatTerm(...splitMinutes(m)) }}
          </button>
          <TermField v-model="termTotal" />
        </span>
        <label><input v-model="termMode" type="radio" value="date" /> 指定日期</label>
        <input v-if="termMode === 'date'" v-model="expiresAt" type="date" />
      </div>
      <label class="ad-f inline">
        <span>延後生效</span>
        <input v-model="notBefore" type="date" />
        <small class="ad-hint">留空＝立即生效。<strong>不影響期限</strong>（期限從今天算起）。</small>
      </label>
      <p v-if="expiresLaterThanLicense" class="ad-warn">
        ⚠️ 加購的到期日<strong>晚於主憑證</strong>。主證失效之後這張加購不會生效
        （產品端不合併已撤銷／失效主證的加購）——確認是刻意的再送出。
      </p>
    </fieldset>

    <fieldset v-if="quotaFields.length" class="ad-box">
      <legend>配額（依各項的合併方式填寫；沒勾的項目完全不受影響）</legend>
      <!-- 批114：這一句原本掛在「進階」那段（兩個獨立數字欄）底下。欄位併進這張表之後
           提醒仍然成立——長期擴充要改主憑證，不是一直疊加購。 -->
      <p class="ad-hint">
        長期要調高的項目請<strong>改主憑證</strong>（換發）而不是加購：加購到期之後客戶會
        突然掉回原本的數字，看起來像我方在扣東西。這裡適合「短期臨時擴充」。
      </p>
      <div :key="`quota-${formKey}`" class="ad-quotas">
        <div v-for="f in quotaFields" :key="f.key" class="ad-q" :class="{ dim: f.deprecated }">
          <label class="ad-q-on">
            <input v-model="quotaOn[f.key]" type="checkbox" />
            <span :title="f.hint ?? f.key">{{ f.label }}</span>
          </label>
          <!-- 加總的鍵在現值後面帶「＋」：輸入框填了數字、placeholder 不見之後，仍讀得出那格是增加量。 -->
          <span class="ad-q-cur">現行 {{ currentQuotaText(f.key) }}{{ quotaMeta[f.key]?.mode === 'sum' ? ' ＋' : '' }}</span>
          <input
            v-model="quotaTo[f.key]"
            type="number"
            :min="quotaAttrs[f.key]?.min ?? 1"
            :step="quotaAttrs[f.key]?.step ?? 1"
            class="ad-num"
            :class="{ bad: !!quotaStepIssues[f.key] }"
            :disabled="!quotaOn[f.key]"
            :placeholder="quotaMeta[f.key]?.inputLabel ?? '目標值'"
            :aria-label="`${f.label}（${quotaMeta[f.key]?.inputLabel ?? '目標值'}）`"
          />
          <!-- 不提供助手的合併方式（min／avg）用 visibility 藏起來：保留位置，同一欄的輸入框才對得齊。 -->
          <span class="ad-q-bump" :class="{ off: !quotaMeta[f.key]?.bump }">
            <button
              v-for="d in [100, 1000]"
              :key="d"
              type="button"
              class="chip pick"
              :disabled="!quotaOn[f.key] || !quotaMeta[f.key]?.bump"
              :tabindex="quotaMeta[f.key]?.bump ? undefined : -1"
              @click="bump(f.key, d)"
            >
              +{{ d }}
            </button>
          </span>
          <small v-if="quotaMeta[f.key]?.note" class="ad-q-merge">{{ quotaMeta[f.key]?.note }}</small>
          <!-- 加購數量級距：獨佔一行（同合併方式的說明），不推動同列的輸入框。 -->
          <small v-if="quotaStepIssues[f.key]" class="ad-q-step">{{ quotaStepIssues[f.key] }}</small>
        </div>
      </div>
    </fieldset>

    <!-- 批114：原本這裡還有一段「進階」——兩個獨立的數字欄。那兩個數字在憑證設計裡本來
         就是配額鍵，上面那張表已經列得到，等於同一個數字有兩個輸入框（而且獨立欄會靜默
         蓋掉表裡填的值）。提醒仍然成立，改寫在配額那一區的 legend 上。 -->

    <div class="ad-grid">
      <label class="ad-f">
        <span>備註</span>
        <input v-model="notes" maxlength="200" placeholder="（會下發到客戶站台，別放內部資訊）" />
      </label>
      <label class="ad-chk">
        <input v-model="trial" type="checkbox" />
        <span>試用性質（到期不告警）</span>
      </label>
    </div>

    <p v-if="unknownModuleKeys.length" class="ad-warn">
      ⚠️ 這些模組鍵不在本產品的憑證設計裡：
      <code v-for="k in unknownModuleKeys" :key="k" class="mono">{{ k }}</code>
      。仍會照簽（憑證設計是活資料），但請確認不是打錯字。
    </p>

    <template #foot>
      <UiButton @click="emit('close')">取消</UiButton>
      <UiButton variant="primary" :disabled="!canSubmit" :loading="busy" @click="submit()">
        簽發加購
      </UiButton>
    </template>
  </UiModal>
</template>

<style scoped>
.ad-note {
  margin: 0 0 var(--sp-3);
  font-size: var(--fs-12);
  color: var(--fg-3);
  line-height: var(--lh-base);
}
.ad-err {
  margin: 0 0 var(--sp-3);
  padding: var(--sp-2) var(--sp-3);
  border-radius: var(--radius-sm);
  background: var(--danger-bg, rgba(220, 38, 38, 0.08));
  color: var(--danger);
  font-size: var(--fs-12);
}
.ad-warn {
  margin: var(--sp-2) 0 0;
  font-size: var(--fs-11);
  color: var(--warning);
  line-height: var(--lh-base);
}
.ad-hint {
  color: var(--fg-4);
  font-size: var(--fs-11);
}
.ad-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--sp-3);
  margin-bottom: var(--sp-3);
}
.ad-f {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: var(--fs-12);
  color: var(--fg-2);
}
/* 批155：「取代」下拉獨佔一列（選項文字含名稱、數值與到期日，半欄放不下）。 */
.ad-f.wide {
  grid-column: 1 / -1;
}
.ad-hint.follow {
  margin: 0 0 var(--sp-2);
  line-height: var(--lh-base);
}
.ad-f.inline {
  flex-direction: row;
  align-items: center;
  gap: var(--sp-2);
  margin-top: var(--sp-2);
}
.ad-chk {
  display: flex;
  align-items: center;
  gap: 6px;
  align-self: end;
  padding-bottom: 6px;
  font-size: var(--fs-12);
  color: var(--fg-2);
}
.ad-box {
  margin: 0 0 var(--sp-3);
  padding: var(--sp-3);
  border: 1px solid var(--border-1);
  border-radius: var(--radius-sm);
}
.ad-box legend {
  padding: 0 6px;
  font-size: var(--fs-12);
  color: var(--fg-3);
}
.ad-term {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--sp-2) var(--sp-3);
  font-size: var(--fs-12);
  color: var(--fg-2);
}
.ad-chips {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.chip.pick {
  cursor: pointer;
  border: 1px solid var(--border-1);
  background: transparent;
  color: var(--fg-3);
}
.chip.pick.on {
  border-color: var(--brand-600, var(--fg-2));
  color: var(--brand-600, var(--fg-1));
  font-weight: 600;
}
.chip.pick:disabled {
  opacity: 0.4;
  cursor: default;
}
.ad-num {
  width: 90px;
  text-align: right;
}
.ad-quotas {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: var(--sp-2) var(--sp-4);
}
.ad-q {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--sp-2);
  font-size: var(--fs-12);
}
/* 批151：非預設合併方式的說明，獨佔一行放在該列底下。 */
.ad-q-merge {
  flex-basis: 100%;
  margin-top: -4px;
  color: var(--fg-4);
  font-size: var(--fs-11);
  line-height: var(--lh-base);
}
/* 加購數量級距不合：同上獨佔一行，紅字。 */
.ad-q-step {
  flex-basis: 100%;
  margin-top: -4px;
  color: var(--danger);
  font-size: var(--fs-11);
  line-height: var(--lh-base);
}
.ad-num.bad {
  border-color: var(--danger);
}
.ad-q.dim {
  opacity: 0.6;
}
.ad-q-on {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex: 1;
  color: var(--fg-2);
  cursor: pointer;
  white-space: nowrap;
}
.ad-q-cur {
  color: var(--fg-4);
  font-size: var(--fs-11);
  white-space: nowrap;
}
.ad-q-bump {
  display: inline-flex;
  gap: 3px;
}
.ad-q-bump.off {
  visibility: hidden;
}
/* 批114：`.ad-adv`（「進階」摺疊區）的樣式隨該區塊一起移除。 */

/* 配額區的整段提示：`.ad-hint` 原本只用在 label 內的一行小字，這裡是獨立段落 */
.ad-box > .ad-hint {
  margin: 0 0 var(--sp-2);
  line-height: var(--lh-base);
}
/* 手機／平板（批171）：配額項目一律頂端對齊——兩欄並排時兩格高度不同（有合併方式說明的那格比較高），
   預設的 stretch 會讓矮的那格內容垂直置中，看起來左右錯位。 */
@media (--compact) {
  .ad-quotas {
    align-items: start;
  }
}
/* 手機（批171）：兩欄收成一欄；下拉與輸入框不超出欄寬（原生 select 的內在寬度＝最長選項）。 */
@media (--mobile) {
  .ad-grid,
  .ad-quotas {
    grid-template-columns: minmax(0, 1fr);
  }
  .ad-f > input,
  .ad-f > select {
    min-width: 0;
    max-width: 100%;
  }
  .ad-f.inline {
    flex-wrap: wrap;
  }
  /* 期限快選膠囊＋自訂欄：一列放不下（「365 天」與自訂欄被切出右緣）→ 換行。 */
  .ad-chips {
    display: flex;
    flex-wrap: wrap;
    row-gap: 6px;
  }
  .ad-box {
    min-width: 0;
    padding: var(--sp-2);
  }
  .ad-q-on {
    flex: 1 1 100%;
    white-space: normal;
  }
}
</style>
